/**
 * Category colours, taken from the design system's colour scale.
 *
 * Values mirror the DS tokens rather than Tailwind's palette: `--orange-100`
 * and `--orange-600` for plan, `--teal-*` for create, `--red-*` for scale,
 * `--violet-*` for deliver, `--green-*` for support. They are inlined as hex
 * because the package exposes the scale neither as CSS custom properties nor
 * through a runtime export, so there is nothing to reference at the call site.
 * Re-point them at the tokens once the package ships them.
 */

export const CATEGORY_COLORS = {
  plan: {
    background: '#feeedc', // --orange-100
    text: '#f18204', // --orange-600
  },
  create: {
    background: '#e8f6f7', // --teal-100
    text: '#4dbac1', // --teal-600
  },
  scale: {
    background: '#ffebf0', // --red-100
    text: '#ff245b', // --red-600
  },
  deliver: {
    background: '#f0ecfe', // --violet-100
    text: '#7252e9', // --violet-600
  },
  support: {
    background: '#e3fcf1', // --green-100
    text: '#04be6d', // --green-700
    icon: '#00dc7c', // --green-600
  },
} as const;

export type CategoryId = keyof typeof CATEGORY_COLORS;

/**
 * The four tool hub categories.
 * Use this anywhere you need to enumerate plan/create/scale/deliver.
 */
export const TOOL_CATEGORIES = [
  'plan',
  'create',
  'scale',
  'deliver',
] as const satisfies readonly Exclude<CategoryId, 'support'>[];

/** A category a tool can belong to — the four above, never `support`. */
export type ToolCategoryId = (typeof TOOL_CATEGORIES)[number];

/**
 * The `Chip` color name that comes closest to each agent category's background, for a widget
 * header chip that has to follow the agent's category rather than assume one.
 *
 * The names look wrong and are not: `Chip` maps its own `blue` to the scale's `lightBlue` family
 * and its `lightBlue` to the scale's `blue` family, so `create` is `'blue'` and `deliver` is
 * `'lightBlue'`. Read the hex values above before "correcting" either one.
 *
 * Nearest-match, not a lookup: `Chip` offers eight named colors and has no teal or rose variant,
 * so only `plan` lands on its exact background. The others are within ~12 RGB units, invisible
 * on tints this pale. Picking the name rather than overriding the color keeps `Chip`'s own dark
 * label color, which the low-contrast icon values above could not provide.
 */
/* eslint-disable i18next/no-literal-string -- Chip component color prop values, never rendered */
export const AGENT_CATEGORY_CHIP_COLORS = {
  plan: 'orange',
  create: 'blue',
  scale: 'red',
  deliver: 'lightBlue',
  support: 'green',
} as const satisfies Record<CategoryId, string>;
/* eslint-enable i18next/no-literal-string */
